import { useState } from 'react';
import { CloudOff, Loader2, RefreshCw, TriangleAlert, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOffline } from '../../lib/offline/offline-context.js';
import { cn } from '@tailoring-crm/ui';
import { QueuedWritesDialog } from './queued-writes-dialog.js';

/**
 * The banner that states the truth.
 *
 * The rule this whole layer exists to honour: never show a spinner the connection cannot
 * resolve. If the shop is offline it says so, says what still works (everything already
 * loaded), and says what will happen to the work being done now (it is saved and will be sent).
 * If a queued write was rejected, that is a separate, louder line — it needs a decision, not
 * patience.
 */
export function ConnectionBanner() {
  const { t } = useTranslation();
  const { status, isOffline, pendingCount, failedCount, flushing, flush } = useOffline();
  const [reviewOpen, setReviewOpen] = useState(false);

  const showSyncing = !isOffline && (flushing || pendingCount > 0);
  const nothingToSay = !isOffline && !showSyncing && failedCount === 0;
  if (nothingToSay) return null;

  return (
    <>
      <div role="status" aria-live="polite" className="flex flex-col">
        {isOffline ? (
          <Banner tone="warning">
            {status === 'offline' ? (
              <WifiOff aria-hidden="true" className="size-4 shrink-0" />
            ) : (
              <CloudOff aria-hidden="true" className="size-4 shrink-0" />
            )}
            <span className="flex-1">
              {status === 'offline' ? t('offline.banner.offline') : t('offline.banner.unreachable')}
              {pendingCount > 0 ? ` ${t('offline.banner.pending', { count: pendingCount })}` : ''}
            </span>
          </Banner>
        ) : null}

        {showSyncing ? (
          <Banner tone="info">
            <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />
            <span className="flex-1">{t('offline.banner.syncing', { count: pendingCount })}</span>
          </Banner>
        ) : null}

        {failedCount > 0 ? (
          <Banner tone="destructive">
            <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
            <span className="flex-1">{t('offline.banner.failed', { count: failedCount })}</span>
            <button
              type="button"
              onClick={() => {
                setReviewOpen(true);
              }}
              className="shrink-0 font-semibold underline underline-offset-2"
            >
              {t('offline.banner.review')}
            </button>
          </Banner>
        ) : null}
      </div>

      {!isOffline && pendingCount > 0 && !flushing ? (
        <button
          type="button"
          onClick={() => {
            void flush();
          }}
          className="flex w-full items-center justify-center gap-2 bg-muted px-4 py-1.5 text-xs font-medium text-muted-foreground"
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          {t('offline.banner.sendNow')}
        </button>
      ) : null}

      <QueuedWritesDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'warning' | 'info' | 'destructive';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm',
        tone === 'warning' && 'bg-warning text-warning-foreground',
        tone === 'info' && 'bg-primary/10 text-foreground',
        tone === 'destructive' && 'bg-destructive text-destructive-foreground',
      )}
    >
      {children}
    </div>
  );
}
